import styled, { css, keyframes } from 'styled-components/macro';
import { darken } from 'polished';
import Button from 'components/base/Button/Button';

import { definitionLabel, linkStyling } from 'styles/base-classes';
import modularScale from 'styles/helpers/modular-scale';
import { CloseButton } from 'components/Glossary/Glossary.style';

export const Container = styled('section')`
  padding: 1em 1em 0;
  background: white;

  @media screen and (min-width: 50em) {
    padding: 1em;
  }

  @media screen and (min-width: 60em) {
    padding: ${({ theme }): string =>
      `${theme.spacing.xlarge} ${theme.spacing.huge} ${theme.spacing.xxlarge}`};
  }
`;

export const Header = styled('header')`
  position: relative;
`;

export const HeaderContent = styled('div')`
  max-width: 30em;
`;

export const Title = styled('h2')`
  font-size: ${modularScale(4)};
  font-weight: 600;
  color: ${({ theme }): string => theme.DeepBlue};
`;

export const Label = styled('p')`
  ${definitionLabel};
`;

const infoFadeIn = keyframes`
  0%{
    opacity: 0;
    transform: translateY(1em);
  }
  100%{
    opacity: 1;
    transform: translateY(0);
  }
`;

const borderOpacity = keyframes`
  0%{
   opacity: 0;
  }
  100%{
    opacity: 1;
  }
`;

export const Info = styled('div')`
  margin-top: ${({ theme }): string => theme.spacing.xlarge};
  margin-bottom: 0;
  text-align: center;
  font-size: ${modularScale(-1)};

  p {
    animation-name: ${infoFadeIn};
    animation-duration: 300ms;
    animation-timing-function: ${({ theme }): string => theme.timingFunction};
    animation-direction: forwards;
    animation-delay: 300ms;
  }
`;

const gridColumns = css`
  display: flex;
  flex-direction: column;

  @media screen and (min-width: 50em) {
    justify-content: space-between;
    flex-direction: row;

    @supports (grid-template-columns: 1fr 1fr) and
      (grid-gap: ${({ theme }): string => theme.spacing.huge}) {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: ${({ theme }): string => theme.spacing.huge};
    }

    > div {
      flex: 0 0 46%;
    }
  }
`;

export const Footer = styled('footer')`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-end;
  position: sticky;
  bottom: 0;
  padding: ${({ theme }): string => theme.spacing.small} 0;
  background: white;
  border-top: 1px solid ${({ theme }): string => theme.LightGrey};

  ${gridColumns};

  > div {
    flex-grow: 1;
  }

  @media screen and (min-width: 50em) {
    left: auto;
    right: auto;
    bottom: auto;
    position: static;
    background: none;
    border-top: 0;
    padding: 0;

    > div {
      flex-grow: 0;
    }

    ${Info} {
      &:before {
        content: '';
        display: block;
        width: 33.333%;
        height: 1px;
        margin: 0 auto;
        margin-bottom: ${({ theme }): string => theme.spacing.large};
        background: ${({ theme }): string => theme.CasperGrey};
        opacity: 0;
        animation-name: ${borderOpacity};
        animation-duration: 300ms;
        animation-timing-function: ${({ theme }): string =>
          theme.timingFunction};
        animation-direction: forwards;
        animation-delay: 300ms;
      }
    }
  }

  ${Button} + ${Button} {
    margin-left: 1em;
  }

  ${Info} {
    flex: 1 0 100%;
    margin-top: 0;
    margin-bottom: ${({ theme }): string => theme.spacing.small};
  }
`;

export const Content = styled('div')`
  margin: ${({ theme }): string => theme.spacing.xlarge} 0;

  ${gridColumns};

  > div:first-child {
    order: 2;
  }

  > p:last-child {
    order: 3;
  }

  @media screen and (min-width: 50em) {
    margin: ${({ theme }): string => theme.spacing.xlarge} 0;

    > div {
      display: flex;
      flex-direction: column;
      order: 1 !important;
    }
  }
`;

export const SaveButton = styled(Button)`
  background: ${({ theme }): string => theme.AccentGreen};
  color: white;
  width: 100%;

  @media screen and (min-width: 50em) {
    width: auto;
  }

  &:hover {
    background: ${({ theme }): string => darken(0.05, theme.AccentGreen)};
  }

  &:focus,
  &:active {
    background: ${({ theme }): string => darken(0.1, theme.AccentGreen)};
  }
`;

export const ResetButton = styled(Button)`
  padding-left: 0;
  padding-right: 0;

  &[disabled] {
    opacity: 0.4;
  }
  &:not([disabled]) {
    &:hover {
      text-decoration: underline;
    }
  }
  @media screen and (max-width: 400px) {
    margin-left: 0 !important;
  }
  @media screen and (min-width: 800px) {
    margin-bottom: 9px !important;
  }
`;

export const ResetButtonContainer = styled('div')`
  display: flex;
  justify-content: flex-end;
  border-bottom: 1px solid ${({ theme }): string => theme.LightGrey};
  border-top: 1px solid ${({ theme }): string => theme.LightGrey};
  padding: ${({ theme }): string => theme.spacing.small} 0;
  margin-bottom: 1em;
  align-items: flex-start;
  flex-direction: column;

  @media screen and (min-width: 50em) {
    border-bottom: 0;
    border-top: 0;
    padding: 0;
    margin-bottom: 0;
    margin-top: ${({ theme }): string => theme.spacing.small};
    justify-content: flex-start;
  }
`;

export const CustomizeDSPsButtonContainer = styled('div')`
  display: flex;
  justify-content: flex-end;
  position: relative;
  z-index: 2;
`;

export const CustomizeDSPsButton = styled('button')`
  appearance: none;
  font-weight: 600;
  color: ${({ theme }): string => theme.NightBlue};
  padding: 0;
  display: flex;
  align-items: center;
  font-size: 0.6875em;

  span {
    ${linkStyling}
  }

  > svg {
    margin-right: ${({ theme }): string => theme.spacing.small};
    margin-left: ${({ theme }): string => theme.spacing.xxsmall};
  }
`;

export const StyledCloseButton = styled(CloseButton)`
  position: absolute;
  right: 0;
  top: 0;
`;

export const WarningMessage = styled('p')`
  font-size: ${modularScale(-1)};
  color: ${({ theme }): string => theme.CasperGrey};

  a {
    color: ${({ theme }): string => theme.CasperGrey};
    text-decoration: underline;

    &:hover,
    &:focus {
      color: ${({ theme }): string => theme.NightBlue};
    }
  }
`;

export const BlankDSPItemWrapper = styled('div')`
  background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='2' ry='2' stroke='%23E5E5E5FF' stroke-width='2' stroke-dasharray='4%2c 10' stroke-dashoffset='1' stroke-linecap='square'/%3e%3c/svg%3e");
  height: 39px;
  padding: 0px 12px;
  margin-bottom: 4px;
  color: #e6e6e6;
  font-size: 30px;
  font-weight: 400;
  line-height: 39px;
  border-radius: 2px;
`;
